import React from "react"
import Layout from "../../components/layout"
import { Link } from "gatsby"

export default props => (
  <Layout location={props.location}>
    <div className="row">
      <div className="col-md-12">
        <div>
          <div className="page-header">
            <h1>Socket.IO</h1>
          </div>
          <ol>
            <li>
              <a href="#section1">Overview</a>
            </li>
            <li>
              <a href="#section2">Connecting to Socket.IO</a>
            </li>
            <li>
              <a href="#section3">The Pub/Sub Model</a>
            </li>
            <li>
              <a href="#section4">Socket.IO Implementation</a>
            </li>
          </ol>
          <br />
          <div id="section1" className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">1 Overview</h4>
            </div>
            <div className="panel-body">
              <p>
                Socket.IO is a javascript library that uses the WebSocket
                protocol to facilitate real time communications. Avaya Spaces
                uses Socket.IO to implement messaging functionality. Every
                message sent (chat, task, or post) is broadcast as a Socket.IO
                event. See the{" "}
                <Link to="/tutorials/messages">tutorial</Link> for
                the step-by-step walkthrough of the connection process, or see
                the{" "}
                <Link to="/api/socket/send_message">
                  Socket.IO reference
                </Link>{" "}
                for full documentation.
              </p>
              <p>
                Avaya Spaces uses version <strong>1.7.3</strong> of Socket.IO.
                Client applications should ensure their implementation of
                Socket.IO is compatible with version <strong>1.7.3</strong>. You
                can obtain the JS script{" "}
                <a href="https://cdnjs.com/libraries/socket.io/1.7.3">here</a>{" "}
                by downloading the file or linking to a CDN.
              </p>
              <p>We recommend the following Socket.IO client libraries:</p>
              <ul>
                {" "}
                <li>
                  <a href="https://cdnjs.com/libraries/socket.io/1.7.3">
                    Javascript
                  </a>
                </li>
                <li>
                  <a href="https://socket.io/blog/native-socket-io-and-android/#">
                    Java/Android client
                  </a>
                </li>
                <li>
                  <a href="https://github.com/socketio/socket.io-client-swift/tree/31adcd4728d2e09285b4dea3d7aadde22866fdea">
                    iOS client
                  </a>
                  {/*MajorVersion 6*/}
                </li>
              </ul>
            </div>
          </div>
          <div id="section2" className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">2 Connecting to Socket.IO</h4>
            </div>
            <div className="panel-body">
              <p>
                Clients are authenticated via JWT or OAuth 2 token. The tokens
                are simply passed to Socket.IO as an argument of the
                IO.connect() function. An example Socket.IO connection request
                is shown below.
              </p>
              <p>Must set websocket only in transports explicitly.</p>
              <pre className="javascript">
                var socketio = io.connect({"\n"}
                {"    "}"https://spacesapis-socket.avayacloud.com/chat",{"\n"}
                {"    "}
                {"{"}query:
                'tokenType=jwt&amp;token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhbm9ueW1vdXNfaWQiOiI1NmRmMzk2MzY3NmU4Mjk0N2IyZjkyNDMiLCJwcm9kdWN0X3R5cGUiOiJvbmVzbmEiLCJpc3MiOlsiZXNuYSIsImNvbSJdLCJpYXQiOjE0NTc0Njk4MDAsImV4cCI6MTQ1NzU1NjIwMH0.can9AMXZuw9NGF1a-qQBxC87q8j4ov7taa86HxIDo9g',
                {"\n"}
                {"     "}transports: ['websocket']{"}"}
                {"\n"}
                {"}"});{"\n"}
              </pre>
              <p>Alternatively, using an OAuth 2 token:</p>
              <pre className="javascript">
                var socketio = io.connect({"\n"}
                {"    "}"https://spacesapis-socket.avayacloud.com/chat",{"\n"}
                {"    "}
                {"{"}query:
                'tokenType=oauth&amp;token=f746696f5de4528u128ae2f274eea253e8a7943a',
                {"\n"}
                {"    "}transports: ['websocket']{"}"}
                {"\n"}
                {"}"});{"\n"}
              </pre>
            </div>
          </div>
          <div id="section3" className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">3 The Pub/Sub Model</h4>
            </div>
            <div className="panel-body">
              <p>
                Avaya Spaces uses a modified{" "}
                <a href="https://en.wikipedia.org/wiki/Publish%E2%80%93subscribe_pattern">
                  Publisher-Subscriber
                </a>{" "}
                (Pub/Sub) model to manage messages. When a Socket.IO connection
                joins a particular Avaya Cloud Space, it becomes both a publisher and
                subscriber to that space. It will be able to broadcast messages
                to others within the space, and will also receive messages from
                others broadcasting within the space. Upon leaving the space, it
                will no longer be able to do either. In a way, this closely
                mirrors the experience of a user sitting at a computer. They may
                have multiple Spaces open in different tabs, each of which can
                send and receive messages, but when they close a tab they won’t
                be able to send or receive messages anymore.
              </p>
              <p>
                Note the critical distinction between users joining a Space and
                socket sessions joining a Space. Users can join or quit spaces
                through the Spaces GUI. When a socket session joins a Space, all
                it’s doing is establishing a socket connection with the Space
                for real time communications purposes.
              </p>
            </div>
          </div>
          <div id="section4" className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">4 Socket.IO Implementation</h4>
            </div>
            <div className="panel-body">
              <p>
                This Pub/Sub functionality is achieved by providing an
                additional layer of abstraction over Socket.IO. Avaya Spaces
                still uses the methods of Socket.IO, but adds authentication,
                authorization, and implements the model described above. Each
                Socket.IO room has a 1 to 1 correspondence to a Avaya Cloud Space. To
                join a space, the socket session must send a{" "}
                <Link to="/api/socket/subscribe_channel">
                  SUBSCRIBE_CHANNEL
                </Link>{" "}
                event, specifying the id of the Space they wish to join. Sending
                messages is easily accomplished with the{" "}
                <Link to="/api/socket/send_message">
                  SEND_MESSAGE
                </Link>{" "}
                event. It’s important to note that the Space ID must still be
                specified in the SEND_MESSAGE event, as a socket session may
                have joined multiple spaces at the same time. To leave a space,
                the session simply broadcasts an{" "}
                <Link to="/api/socket/unsubscribe_channel">
                  UNSUBSCRIBE_CHANNEL
                </Link>{" "}
                event.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
)
